import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

/**
 * Featured-placement lifecycle state — mirrors `PlacementStatus` from
 * `@bravemove/types/featured-placement`. Kept local — `@bravemove/types` is NOT a dep of
 * `@bravemove/ui-web`. Spec source:
 * [`docs/design/screens/F39-components.jsx`](../../../../../docs/design/screens/F39-components.jsx)
 * `placementStateCfg` (line 81).
 */
export type PlacementState =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'scheduled'
  | 'active'
  | 'paused'
  | 'rejected'
  | 'expired'
  | 'suspended_by_admin';
export type PlacementStateBadgeSize = 'sm' | 'md';

export interface PlacementStateBadgeProps extends Omit<
  HTMLAttributes<HTMLSpanElement>,
  'children'
> {
  readonly state: PlacementState;
  readonly size?: PlacementStateBadgeSize;
  /** Override the default state label. */
  readonly label?: string;
}

interface PlacementStateStyle {
  readonly box: string;
  readonly dot: string;
  readonly defaultLabel: string;
  /** `active` pulses (motion-safe) to read as live. */
  readonly pulse: boolean;
}

const STATE_STYLE: Record<PlacementState, PlacementStateStyle> = {
  draft: {
    box: 'bg-surface-2 text-ink-3 border border-hairline-2',
    dot: 'bg-ink-3',
    defaultLabel: 'Draft',
    pulse: false,
  },
  pending_approval: {
    box: 'bg-warning/15 text-ink border border-warning/40',
    dot: 'bg-warning',
    defaultLabel: 'Pending',
    pulse: false,
  },
  approved: {
    box: 'bg-success/15 text-ink border border-success/40',
    dot: 'bg-success',
    defaultLabel: 'Approved',
    pulse: false,
  },
  scheduled: {
    box: 'bg-info/15 text-ink border border-info/40',
    dot: 'bg-info',
    defaultLabel: 'Scheduled',
    pulse: false,
  },
  active: {
    box: 'bg-success/15 text-ink border border-success/40',
    dot: 'bg-success',
    defaultLabel: 'Active',
    pulse: true,
  },
  paused: {
    box: 'bg-surface-2 text-ink-3 border border-hairline-2',
    dot: 'bg-ink-3',
    defaultLabel: 'Paused',
    pulse: false,
  },
  rejected: {
    box: 'bg-danger/15 text-ink border border-dashed border-danger',
    dot: 'bg-danger',
    defaultLabel: 'Rejected',
    pulse: false,
  },
  expired: {
    box: 'bg-surface-2 text-ink-3 border border-hairline-2',
    dot: 'bg-ink-3',
    defaultLabel: 'Expired',
    pulse: false,
  },
  suspended_by_admin: {
    box: 'bg-danger/15 text-ink border border-dashed border-danger',
    dot: 'bg-danger',
    defaultLabel: 'Suspended',
    pulse: false,
  },
};

const SIZE: Record<PlacementStateBadgeSize, { box: string; dot: string }> = {
  sm: { box: 'px-[7px] py-[2px] gap-1.5 text-[9px]', dot: 'size-[5px]' },
  md: { box: 'px-[9px] py-[4px] gap-1.5 text-[10px]', dot: 'size-[6px]' },
};

/**
 * F39 — `PlacementStateBadge` (shared primitive). Featured-placement lifecycle
 * pill across the venue placement dashboard + admin approval queue + monitor.
 * `active` pulses; `rejected` + `suspended_by_admin` carry a dashed border.
 *
 * @example
 *   <PlacementStateBadge state="active" />
 *   <PlacementStateBadge state="pending_approval" size="sm" />
 */
export function PlacementStateBadge({
  state,
  size = 'md',
  label,
  className,
  ...rest
}: PlacementStateBadgeProps) {
  const style = STATE_STYLE[state];
  const sz = SIZE[size];
  const text = label ?? style.defaultLabel;
  return (
    <span
      role="img"
      aria-label={`Placement state: ${text}`}
      data-testid={`placement-state-badge-${state}`}
      className={cn(
        'inline-flex items-center rounded uppercase whitespace-nowrap align-middle font-mono font-extrabold tracking-[0.1em]',
        style.box,
        sz.box,
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block rounded-full shrink-0',
          sz.dot,
          style.dot,
          style.pulse && 'motion-safe:animate-pulse',
        )}
      />
      <span>{text}</span>
    </span>
  );
}
